import { createContext, useContext, useEffect, useState } from 'react';
import { buildTree, type PageNode, type PageRecord, type Store, type Topic } from '@nexus/store';
import type { OpenedStore } from './open';

const StoreContext = createContext<OpenedStore | null>(null);

export const StoreProvider = StoreContext.Provider;

export function useOpenedStore(): OpenedStore {
  const s = useContext(StoreContext);
  if (!s) throw new Error('StoreProvider missing');
  return s;
}

export const useStore = (): Store => useOpenedStore().store;

const COALESCE_MS = 40;

/** Re-runs `load` whenever the store reports a change in one of `topics`. */
export function useStoreQuery<T>(
  load: (store: Store) => Promise<T>,
  deps: unknown[] = [],
  topics?: Topic[],
): T | undefined {
  const store = useStore();
  const [value, setValue] = useState<T>();
  useEffect(() => {
    let cancelled = false;
    let running = false;
    let dirty = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    // Bursts of changes (typing, imports) collapse into at most one running
    // query plus one follow-up, instead of one query per change.
    const run = () => {
      if (running) {
        dirty = true;
        return;
      }
      running = true;
      void load(store)
        .then((v) => {
          if (!cancelled) setValue(v);
        })
        .finally(() => {
          running = false;
          if (dirty && !cancelled) {
            dirty = false;
            schedule();
          }
        });
    };
    const schedule = () => {
      clearTimeout(timer);
      timer = setTimeout(run, COALESCE_MS);
    };
    run();
    const off = store.subscribe(schedule, topics);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      off();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, ...deps]);
  return value;
}

/**
 * `list`/`byId` cover every page (personal and every workspace) so search,
 * link autocomplete, backlinks and the graph keep working across all of
 * them; `tree` is the personal page tree only -- shared workspaces get
 * their own tree each, in the sidebar's separate Workspaces section.
 */
export function usePages():
  { list: PageRecord[]; tree: PageNode[]; byId: Map<string, PageRecord> } | undefined {
  return useStoreQuery(
    async (s) => {
      const list = await s.listPages();
      return {
        list,
        tree: buildTree(list.filter((p) => p.workspaceId === null)),
        byId: new Map(list.map((p) => [p.id, p])),
      };
    },
    [],
    ['pages'],
  );
}
